import { cn } from "@utils";
import { ExternalLink, MessageCircleQuestion } from "lucide-react";
import { DiffBlock } from "src/components/ui/DiffBlock";
import { ChangeManagementOperation } from "src/types/change-management";
import {
  getOperationDiffLines,
  getOperationPreview,
} from "./change-management-plan";
import { CollapseToggle } from "./CollapseToggle";
import { EvidenceLink } from "./EvidenceLink";
import { ReviewActionPlaceholders } from "./ReviewActionPlaceholders";
import { ViewedCheckbox } from "./ViewedCheckbox";

const chipClassName =
  "rounded bg-gray-100 px-1.5 font-mono text-xs leading-[1.66] text-black/60";

type DataKeyOperation = Extract<
  ChangeManagementOperation,
  { data_key: string }
>;

const isDataKeyOperation = (
  operation: ChangeManagementOperation
): operation is DataKeyOperation => "data_key" in operation;

const DataKeyHeading = ({
  operation,
  label,
}: {
  operation: DataKeyOperation;
  label: string | null;
}) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
    {label && <span className="font-semibold">{label}</span>}
    <code className={cn(chipClassName, "text-black/[.87]")}>
      {operation.data_key}
    </code>
  </div>
);

const OperationBody = ({
  operation,
}: {
  operation: ChangeManagementOperation;
}) => {
  switch (operation.type) {
    case "human_review":
      return (
        <div className="flex max-w-[78ch] items-start gap-2 rounded bg-sky-50 px-3 py-2 text-sky-900">
          <MessageCircleQuestion
            size={14}
            className="mt-[3px] shrink-0 text-sky-600"
          />
          <span>{operation.question}</span>
        </div>
      );
    case "create_document":
      return operation.template_to_create ? (
        <span className="flex flex-wrap items-center gap-1.5 text-sm">
          Create from template
          <code className={cn(chipClassName, "text-black/[.87]")}>
            {operation.template_to_create}
          </code>
        </span>
      ) : (
        <span className="text-sm">Custom document (no template)</span>
      );
    default:
      return <DiffBlock lines={getOperationDiffLines(operation)} />;
  }
};

export const OperationCard = ({
  operation,
  viewed,
  collapsed,
  onViewedChange,
  onToggle,
  getDataKeyLabel,
  getEvidenceLabel,
}: {
  operation: ChangeManagementOperation;
  viewed: boolean;
  collapsed: boolean;
  onViewedChange: (viewed: boolean) => void;
  onToggle: () => void;
  getDataKeyLabel: (dataKey: string) => string | null;
  getEvidenceLabel: (sourceUrl: string) => string;
}) => {
  const heading = isDataKeyOperation(operation) ? (
    <DataKeyHeading
      operation={operation}
      label={getDataKeyLabel(operation.data_key)}
    />
  ) : null;

  return (
    <div
      data-change-card
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-2 border-0 border-t border-solid border-black/[.12] pl-[44px] pr-4",
        collapsed ? "py-3" : "py-5"
      )}
    >
      <div
        className={cn(
          "flex min-w-0 flex-[1_1_320px] flex-col gap-2",
          collapsed && "opacity-80"
        )}
      >
        {heading}
        {collapsed ? (
          !heading && (
            <div className="truncate text-black/60">
              {getOperationPreview(operation)}
            </div>
          )
        ) : (
          <>
            <OperationBody operation={operation} />
            <div className="flex items-start gap-2.5 text-sm leading-[1.6] text-black/60">
              <span className="shrink-0 text-sm font-semibold leading-[1.6]">
                Why
              </span>
              <div className="flex min-w-0 flex-col gap-1.5">
                <div>{operation.why}</div>
                {operation.source_url && (
                  <div className="flex">
                    <EvidenceLink
                      href={operation.source_url}
                      label={getEvidenceLabel(operation.source_url)}
                      icon={ExternalLink}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-0.5">
        {!collapsed && <ReviewActionPlaceholders showReject compact />}
        <ViewedCheckbox checked={viewed} onChange={onViewedChange}>
          Viewed
        </ViewedCheckbox>
        <CollapseToggle
          collapsed={collapsed}
          label="change"
          onToggle={onToggle}
        />
      </div>
    </div>
  );
};
